// Extends the existing VariantSelects class with enhanced functionality
// This script should be loaded after global.js

(function() {
  'use strict';

  const selectors = {
    variantSelects: 'variant-selects',
    optionGroups: 'fieldset, .product-form__input--dropdown',
    radioInputs: 'input[type="radio"]',
    selects: 'select',
    options: 'option',
    labels: 'label'
  };

  function initRbVariantSelects() {
    const variantSelects = document.querySelectorAll(selectors.variantSelects);

    if (variantSelects.length === 0) {
      setTimeout(initRbVariantSelects, 100);
      return;
    }

    variantSelects.forEach(enhanceVariantSelects);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'VARIANT-SELECTS') {
              enhanceVariantSelects(node);
            }
            const children = node.querySelectorAll(selectors.variantSelects);
            children.forEach(enhanceVariantSelects);
          }
        });
      });
    }).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function enhanceVariantSelects(element) {
    if (element._rbEnhanced) return;
    element._rbEnhanced = true;
    element.addEventListener('change', handleEnhancedChange);
    updateOptionAvailability(element);

    function handleEnhancedChange(event) {
      const target = element.getInputForEventTarget(event.target);
      element.lastChangedOption = getChangedOptionFromEvent(event, target);

      if (validateVariantSelection(element)) {
        updateOptionAvailability(element);
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: { event, target, selectedOptionValues: element.selectedOptionValues }
        });
      } else {
        updateOptionAvailability(element);
      }
    }
  }

  function isMatrixFilterDisabled(element) {
    return element?.dataset?.skipVariantMatrixFilter === 'true';
  }

  function validateVariantSelection(element) {
    if (isMatrixFilterDisabled(element)) return true;

    const productVariants = getProductVariants();
    if (!productVariants?.length) return true;

    const currentOptions = getCurrentOptionValues(element);
    const matchingVariant = findMatchingVariant(currentOptions, productVariants);

    if (!matchingVariant) {
      const alternativeVariant = findVariantWithChangedOption(currentOptions, productVariants, element);
      if (alternativeVariant) {
        selectVariant(alternativeVariant, element);
        return false;
      }
      return false;
    }

    return true;
  }

  function findVariantWithChangedOption(currentOptions, productVariants, element) {
    const changedOptionIndex = getChangedOptionIndex(element);
    if (changedOptionIndex === -1) return null;

    const changedOptionValue = currentOptions[changedOptionIndex];
    const matchingVariants = productVariants.filter((variant) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      return variantOptions[changedOptionIndex] === changedOptionValue;
    });

    if (!matchingVariants.length) return null;

    const availableVariants = matchingVariants.filter((variant) => variant.available);
    const candidateVariants = availableVariants.length ? availableVariants : matchingVariants;

    return candidateVariants.reduce((best, variant) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      const matchCount = currentOptions.reduce(
        (count, option, index) => count + (variantOptions[index] === option ? 1 : 0),
        0
      );
      return matchCount > best.matchCount ? { variant, matchCount } : best;
    }, { variant: null, matchCount: -1 }).variant;
  }

  function getChangedOptionFromEvent(event, target) {
    let optionIndex = -1;
    let optionValue = null;

    if (target.tagName === 'SELECT') {
      optionValue = target.value;
      const allInputs = target.closest(selectors.variantSelects).querySelectorAll('select, fieldset');
      optionIndex = Array.from(allInputs).findIndex((input) => input === target || input.contains(target));
    } else if (target.type === 'radio') {
      optionValue = target.value;
      const fieldset = target.closest('fieldset');
      if (fieldset) {
        const allFieldsets = target.closest(selectors.variantSelects).querySelectorAll('fieldset');
        optionIndex = Array.from(allFieldsets).findIndex((fs) => fs === fieldset);
      }
    }

    return { optionIndex, optionValue, target };
  }

  function getChangedOptionIndex(element) {
    if (element.lastChangedOption?.optionIndex >= 0) {
      return element.lastChangedOption.optionIndex;
    }

    const optionGroups = element.querySelectorAll(selectors.optionGroups);
    let lastSelectedIndex = -1;
    optionGroups.forEach((optionGroup, index) => {
      if (getSelectedValueForOptionGroup(optionGroup)) {
        lastSelectedIndex = index;
      }
    });
    return lastSelectedIndex;
  }

  function updateOptionAvailability(element) {
    if (isMatrixFilterDisabled(element)) return;

    const productVariants = getProductVariants();
    if (!productVariants?.length) return;

    const currentOptions = getCurrentOptionValues(element);
    const optionGroups = element.querySelectorAll(selectors.optionGroups);

    optionGroups.forEach((optionGroup, groupIndex) => {
      if (groupIndex > 0) {
        updateOptionGroupAvailability(optionGroup, groupIndex, currentOptions, productVariants);
      } else {
        ensureAllOptionsVisible(optionGroup);
      }
    });
  }

  function updateOptionGroupAvailability(optionGroup, groupIndex, currentOptions, productVariants) {
    const optionValues = getOptionValuesForGroup(optionGroup);
    optionValues.forEach((optionValue) => {
      const { compatible, inStock } = getOptionValueState(optionValue, groupIndex, currentOptions, productVariants);
      setOptionState(optionGroup, optionValue, { compatible, inStock });
    });
  }

  function getOptionValuesForGroup(optionGroup) {
    const values = [];

    if (optionGroup.tagName === 'FIELDSET') {
      const radioInputs = optionGroup.querySelectorAll(selectors.radioInputs);
      radioInputs.forEach((radio) => values.push(radio.value));
    } else {
      const select = optionGroup.querySelector(selectors.selects);
      if (select) {
        const options = select.querySelectorAll(selectors.options);
        options.forEach((option) => {
          if (option.value) values.push(option.value);
        });
      }
    }

    return values;
  }

  function findMatchingVariant(optionValues, productVariants) {
    return productVariants.find((variant) => optionsMatchVariant(optionValues, variant)) || null;
  }

  function getOptionValueState(optionValue, groupIndex, currentOptions, productVariants) {
    if (groupIndex === 0) {
      return { compatible: true, inStock: true };
    }

    const testOptions = [...currentOptions];
    testOptions[groupIndex] = optionValue;
    const variant = findMatchingVariant(testOptions, productVariants);
    return {
      compatible: !!variant,
      inStock: variant?.available === true
    };
  }

  function getSelectedValueForOptionGroup(optionGroup) {
    if (optionGroup.tagName === 'FIELDSET') {
      const checked = optionGroup.querySelector(`${selectors.radioInputs}:checked`);
      return checked?.value ?? '';
    }

    const select = optionGroup.querySelector(selectors.selects);
    return select?.value ?? '';
  }

  function setOptionState(optionGroup, optionValue, { compatible, inStock }) {
    if (optionGroup.tagName === 'FIELDSET') {
      setRadioState(optionGroup, optionValue, compatible, inStock);
    } else {
      setSelectState(optionGroup, optionValue, compatible, inStock);
    }
  }

  function findRadioByValue(optionGroup, optionValue) {
    const radioInputs = optionGroup.querySelectorAll(selectors.radioInputs);
    return Array.from(radioInputs).find((radio) => radio.value === optionValue);
  }

  function findSelectOptionByValue(optionGroup, optionValue) {
    const select = optionGroup.querySelector(selectors.selects);
    if (!select) return null;
    const options = select.querySelectorAll(selectors.options);
    return Array.from(options).find((option) => option.value === optionValue);
  }

  function setRadioState(optionGroup, optionValue, compatible, inStock) {
    const radioInput = findRadioByValue(optionGroup, optionValue);
    if (!radioInput) return;

    const label = optionGroup.querySelector(`label[for="${radioInput.id}"]`);

    if (!compatible) {
      radioInput.hidden = true;
      radioInput.setAttribute('data-incompatible', 'true');
      radioInput.removeAttribute('data-unavailable');
      radioInput.classList.add('disabled');
      radioInput.disabled = true;
      if (label) {
        label.hidden = true;
        label.setAttribute('data-incompatible', 'true');
        label.setAttribute('aria-hidden', 'true');
        label.removeAttribute('data-unavailable');
        label.classList.add('unavailable');
        const unavailableText = label.querySelector('.label-unavailable');
        if (unavailableText) unavailableText.style.display = 'none';
      }
      return;
    }

    radioInput.hidden = false;
    radioInput.removeAttribute('data-incompatible');
    if (label) {
      label.hidden = false;
      label.removeAttribute('data-incompatible');
      label.removeAttribute('aria-hidden');
    }

    if (inStock) {
      radioInput.classList.remove('disabled');
      radioInput.removeAttribute('data-unavailable');
      radioInput.disabled = false;
      if (label) {
        label.removeAttribute('data-unavailable');
        label.classList.remove('unavailable');
        const unavailableText = label.querySelector('.label-unavailable');
        if (unavailableText) unavailableText.style.display = 'none';
      }
    } else {
      radioInput.classList.add('disabled');
      radioInput.setAttribute('data-unavailable', 'true');
      radioInput.disabled = true;
      if (label) {
        label.setAttribute('data-unavailable', 'true');
        label.classList.add('unavailable');
        const unavailableText = label.querySelector('.label-unavailable');
        if (unavailableText) unavailableText.style.display = 'inline';
      }
    }
  }

  function setSelectState(optionGroup, optionValue, compatible, inStock) {
    const option = findSelectOptionByValue(optionGroup, optionValue);
    if (!option) return;

    if (!compatible) {
      option.hidden = true;
      option.setAttribute('data-incompatible', 'true');
      option.removeAttribute('data-unavailable');
      option.disabled = true;
      option.classList.add('unavailable');
      if (option.textContent.includes('unavailable')) {
        option.textContent = optionValue;
      }
      return;
    }

    option.hidden = false;
    option.removeAttribute('data-incompatible');

    if (inStock) {
      option.disabled = false;
      option.removeAttribute('data-unavailable');
      option.classList.remove('unavailable');
      if (option.textContent.includes('unavailable')) {
        option.textContent = optionValue;
      }
    } else {
      option.disabled = true;
      option.setAttribute('data-unavailable', 'true');
      option.classList.add('unavailable');
      if (!option.textContent.includes('unavailable')) {
        option.textContent = `${optionValue} - unavailable`;
      }
    }
  }

  function ensureAllOptionsVisible(optionGroup) {
    const optionValues = getOptionValuesForGroup(optionGroup);
    optionValues.forEach((optionValue) => {
      setOptionState(optionGroup, optionValue, { compatible: true, inStock: true });
    });
  }

  function getProductVariants() {
    const scripts = document.querySelectorAll('script:not([src])');
    for (const script of scripts) {
      const match = script.textContent.match(/const productVariants = (\[.*?\]);/s);
      if (match) return JSON.parse(match[1]);
    }
    return null;
  }

  function selectVariant(variant, element) {
    const variantOptions = [variant.option1, variant.option2, variant.option3];
    const optionGroups = element.querySelectorAll(selectors.optionGroups);

    variantOptions.forEach((optionValue, index) => {
      if (!optionValue) return;

      const optionGroup = optionGroups[index];
      if (!optionGroup) return;

      if (optionGroup.tagName === 'FIELDSET') {
        const matchingRadio = findRadioByValue(optionGroup, optionValue);
        if (matchingRadio && !matchingRadio.checked) {
          matchingRadio.checked = true;
          matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        const select = optionGroup.querySelector(selectors.selects);
        if (select && select.value !== optionValue) {
          select.value = optionValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  function getCurrentOptionValues(element) {
    const optionGroups = element.querySelectorAll(selectors.optionGroups);
    return Array.from(optionGroups, getSelectedValueForOptionGroup);
  }

  function optionsMatchVariant(optionValues, variant) {
    const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    if (optionValues.length !== variantOptions.length) return false;
    return optionValues.every((optionValue, index) => variantOptions[index] === optionValue);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRbVariantSelects);
  } else {
    initRbVariantSelects();
  }

  window.addEventListener('load', initRbVariantSelects);
})();
